// Slides for the sign-up / login showcase panel.
//
// Transcribed from the Figma board "Investor Web app-21/09/2026" (the five
// `Component 19` variants). The artwork here is the PHOTOGRAPH ONLY — the
// glass cards, headline and dots are rendered as live DOM by
// `AuthShowcaseCarousel`.
//
// That split is what makes the panel viewport-independent. Text baked into a
// fixed 708×945 image cannot reflow, so on any window shorter than the board
// — which is every real browser window, Mac included — the headline was
// cropped away. As live DOM it repositions instead.
//
// COORDINATE SYSTEM — every `x` / `y` / `width` below is a percentage of the
// 708×945 artboard, so a card lands in the same relative spot whatever size
// the panel renders at. The original Figma pixel value is in the trailing
// comment. Type and padding scale separately, via the `--u` design-unit in
// the component.

import photoTitleDeeds from '@assets/images/authImages/photo-1.png'
import photoVaraLicensed from '@assets/images/authImages/photo-2.png'
import photoBlockchain from '@assets/images/authImages/photo-3.png'
import photoMinimumTicket from '@assets/images/authImages/photo-4.png'
import photoRentalIncome from '@assets/images/authImages/photo-5.png'

import badgeDubaiLand from '@assets/images/authImages/badge-dubai-land.png'
import badgeVara from '@assets/images/authImages/badge-vara.png'
import badgeXrpLedger from '@assets/images/authImages/badge-xrp-ledger.png'
import badgeDubaiRest from '@assets/images/authImages/badge-dubai-rest.png'
import iconRentalIndex from '@assets/images/authImages/icon-rental-index.svg'

/** One run of headline text. Which half is brand-violet flips per slide. */
export interface ShowcaseTitleSegment {
  text: string
  /** Render in brand violet instead of white. */
  accent?: boolean
  /**
   * Force this segment onto its own line. The board hard-breaks only two
   * headlines; the rest are a single run that wraps naturally, so defaulting
   * every segment to its own line (as the first pass did) pushed longer
   * headlines onto a third line and into the floating cards.
   */
  newLine?: boolean
}

interface ShowcaseCardPosition {
  /** Left edge, % of artboard width. */
  x: number
  /** Top edge, % of artboard height. */
  y: number
  /** Glass fill opacity — the board varies this per card. */
  fill: number
}

/** Icon + figure, e.g. "269 Investors". */
export interface ShowcaseStatCard extends ShowcaseCardPosition {
  kind: 'stat'
  icon: 'investors' | 'token'
  label: string
}

/** Tinted icon tile, optional delta pill, figure and caption. */
export interface ShowcaseMetricCard extends ShowcaseCardPosition {
  kind: 'metric'
  tile: 'money' | 'property'
  value: string
  caption: string
  /** Signed change shown as a green pill, e.g. "+6.25%". */
  delta?: string
  /** Fixed inner width, % of artboard width. Omit to size to content. */
  width?: number
}

/** A partner mark on a glass plate. */
export interface ShowcaseLogoCard extends ShowcaseCardPosition {
  kind: 'logo'
  src: string
  alt: string
  /** % of artboard width. */
  width: number
}

export type ShowcaseCard = ShowcaseStatCard | ShowcaseMetricCard | ShowcaseLogoCard

export interface ShowcaseSlide {
  id: string
  title: ShowcaseTitleSegment[]
  description: string
  /** Photograph, with the board's gradient scrim already applied. */
  photo: string
  cards: ShowcaseCard[]
}

export const SHOWCASE_SLIDES: ShowcaseSlide[] = [
  {
    id: 'title-deeds',
    title: [
      { text: 'DLD Approved', accent: true },
      { text: 'Title Deeds', newLine: true },
    ],
    description: 'Ensures your property ownership is legally protected and indisputable.',
    photo: photoTitleDeeds,
    cards: [
      { kind: 'stat', icon: 'investors', label: '269 Investors', x: 39.12, y: 27.72, fill: 0.32 }, // 277,262
      {
        kind: 'metric',
        tile: 'property',
        value: '73 Properties',
        caption: 'DLD registered',
        x: 62.29,
        y: 48.47,
        fill: 0.32,
      }, // 441,458
      {
        kind: 'logo',
        src: badgeDubaiLand,
        alt: 'Dubai Land Department',
        width: 19.35,
        x: 15.4,
        y: 48.47,
        fill: 0.54,
      }, // 109,458
    ],
  },
  {
    id: 'vara-licensed',
    title: [
      { text: 'Fully Regulated,', accent: true },
      { text: 'VARA Licensed', newLine: true },
    ],
    description: 'VARA license ensures regulated, secure, and legal virtual asset transactions.',
    photo: photoVaraLicensed,
    cards: [
      { kind: 'stat', icon: 'token', label: '285 Tokens', x: 10.17, y: 37.27, fill: 0.42 }, // 72,352.2
      {
        kind: 'logo',
        src: badgeVara,
        alt: 'VARA — Virtual Assets Regulatory Authority',
        width: 11.57,
        x: 53.53,
        y: 27.83,
        fill: 0.58,
      }, // 379,263
      { kind: 'stat', icon: 'investors', label: '269 Investors', x: 59.5, y: 52.79, fill: 0.42 }, // 421.28,498.84
    ],
  },
  {
    id: 'blockchain',
    title: [{ text: 'Blockchain Based ' }, { text: 'Fractional Investing', accent: true }],
    description:
      'Blockchain technology provides transparent, immutable, and secure digital property records.',
    photo: photoBlockchain,
    cards: [
      {
        kind: 'metric',
        tile: 'money',
        value: 'Ð 2.50',
        caption: 'Per Token',
        delta: '+8.68%',
        width: 21.47,
        x: 10.73,
        y: 39.81,
        fill: 0.42,
      }, // 76,376.57 · w152
      {
        kind: 'logo',
        src: badgeXrpLedger,
        alt: 'XRP Ledger',
        width: 17.09,
        x: 51.69,
        y: 24.21,
        fill: 0.52,
      }, // 366,229
      { kind: 'stat', icon: 'investors', label: '269 Investors', x: 60.07, y: 55.31, fill: 0.42 }, // 425.28,523.21
    ],
  },
  {
    id: 'minimum-ticket',
    title: [{ text: 'Invest in UAE’s Best Properties from ' }, { text: 'AED 2000', accent: true }],
    description:
      'With a minimum investment of just AED 2000, you can own your share of a property through Sliceal.',
    photo: photoMinimumTicket,
    cards: [
      { kind: 'stat', icon: 'token', label: '123 Tokens', x: 10.73, y: 39.81, fill: 0.42 }, // 76,376.57
      { kind: 'stat', icon: 'investors', label: '269 Investors', x: 54.1, y: 25.09, fill: 0.42 }, // 383,237.37
      {
        kind: 'metric',
        tile: 'money',
        value: 'Ð 3.9M',
        caption: 'Property value',
        delta: '+12.13%',
        width: 21.47,
        x: 62.57,
        y: 54.33,
        fill: 0.42,
      }, // 443,514 · w152
    ],
  },
  {
    id: 'rental-income',
    title: [
      { text: 'Rental Income', accent: true },
      { text: ' every month, Appreciation over time' },
    ],
    description:
      'You receive a net return on investment from your investments. The net return you receive comes from appreciation and a fixed rent over time.',
    photo: photoRentalIncome,
    cards: [
      {
        kind: 'metric',
        tile: 'money',
        value: 'Ð 4,891',
        caption: 'Total Rental income',
        delta: '+6.25%',
        x: 43.08,
        y: 17.23,
        fill: 0.42,
      }, // 305,163
      {
        kind: 'logo',
        src: iconRentalIndex,
        alt: 'Dubai rental index',
        width: 7.91,
        x: 19.49,
        y: 44.29,
        fill: 0.72,
      }, // 138,419
      {
        kind: 'logo',
        src: badgeDubaiRest,
        alt: 'Dubai REST',
        width: 7.59,
        x: 60.17,
        y: 56.45,
        fill: 0.72,
      }, // 426,534
    ],
  },
]

/** Auto-advance interval for the showcase carousel. */
export const SHOWCASE_SLIDE_INTERVAL_MS = 3000
